import { inject } from '@angular/core';
import { RectilinearView, Scene } from 'marzipano';
import { LoggingService } from '@newroom-connect/library/services';

import { IMarzipanoViewState, IMarzipanoViewParams } from '../interfaces/marzipano-scene.interface';

/**
 * Marzipano View Manager.
 *
 * Handles all view-related operations for Marzipano scenes.
 * Responsibilities.
 * - View state management (yaw, pitch, fov, roll)
 * - View animations and transitions.
 * - Camera state retrieval.
 * - View parameter updates.
 */
export class MarzipanoView {
  private readonly logger = inject(LoggingService);

  private scene: Scene | null = null;
  private view: RectilinearView | null = null;

  /**
   * Initialize the view manager with Marzipano scene and view instances.
   *
   * @param scene - The Marzipano scene instance.
   * @param view - The RectilinearView instance.
   */
  public initialize(scene: Scene, view: RectilinearView): void {
    this.scene = scene;
    this.view = view;
    this.logger.debug('MarzipanoView initialized');
  }

  /**
   * Set view parameters (yaw, pitch, fov, roll) immediately without animation.
   * Extracted from MarzipanoScene.setViewState().
   *
   * @param viewState - Partial view state to update.
   * @param onViewChange - Callback to emit view change events.
   *
   * @throws {Error} If view is not initialized.
   */
  public setViewState(viewState: Partial<IMarzipanoViewState>, onViewChange?: (viewState: IMarzipanoViewState) => void): void {
    if (!this.view) {
      this.logger.warn('Cannot set view state: view not initialized');
      return;
    }

    try {
      const currentParams = this.view.parameters();
      const newParams: IMarzipanoViewParams = {
        yaw: viewState.yaw ?? currentParams.yaw,
        pitch: viewState.pitch ?? currentParams.pitch,
        fov: viewState.fov ?? (currentParams.fov || Math.PI / 3),
        roll: viewState.roll ?? (currentParams.roll || 0)
      };

      this.view.setParameters(newParams);

      // Notify parent of view change
      if (onViewChange) {
        onViewChange(this.getCurrentCameraState());
      }

      this.logger.debug('View state updated', newParams);
    } catch (error) {
      this.logger.error('Failed to set view state', error as Error);
      throw error; // Let parent handle error emission
    }
  }

  /**
   * Animate to specific view state with smooth transition.
   * Extracted from MarzipanoScene.animateToViewState().
   *
   * @param viewState - Target view state to animate to.
   * @param onViewChange - Callback to emit view change events.
   * @param duration - Animation duration in milliseconds (default: 1000).
   * @returns Promise that resolves when animation completes.
   */
  public   async animateToViewState(viewState: Partial<IMarzipanoViewState>, onViewChange?: (viewState: IMarzipanoViewState) => void, duration = 1000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.scene) {
        reject(new Error('Scene not ready for animation'));
        return;
      }

      try {
        const targetParams: IMarzipanoViewParams = {
          yaw: viewState.yaw ?? 0,
          pitch: viewState.pitch ?? 0,
          fov: viewState.fov ?? Math.PI / 3
        };

        // Use scene lookTo method for smooth animation
        this.scene.lookTo(targetParams, {
          transitionDuration: duration
        }, (error?: Error) => {
          if (error) {
            this.logger.error('Animation failed', error);
            reject(error);
          } else {
            // Notify parent of view change
            if (onViewChange) {
              onViewChange(this.getCurrentCameraState());
            }
            resolve();
          }
        });
      } catch (error) {
        this.logger.error('Failed to start animation', error as Error);
        reject(error);
      }
    });
  }

  /**
   * Get current camera state with spherical coordinates.
   * Extracted from MarzipanoScene.getCurrentCameraState().
   *
   * @returns Current camera state with spherical coordinates (yaw, pitch, fov, roll).
   */
  public getCurrentCameraState(): IMarzipanoViewState {
    if (!this.view) {
      return {
        yaw: 0,
        pitch: 0,
        fov: Math.PI / 3 // 60 degrees default
      };
    }

    const params = this.view.parameters();

    return {
      yaw: params.yaw,
      pitch: params.pitch,
      fov: params.fov || Math.PI / 3,
      roll: params.roll || 0,
      viewport: {
        width: this.view.width(),
        height: this.view.height()
      }
    };
  }

  /**
   * Get the current view parameters directly from the Marzipano view.
   * Useful for internal operations that need raw Marzipano parameters.
   *
   * @returns Current Marzipano view parameters or null if not initialized.
   */
  public getCurrentViewParameters(): IMarzipanoViewParams | null {
    if (!this.view) {
      return null;
    }

    const viewParams: IMarzipanoViewParams = {
      yaw: this.view.parameters().yaw,
      pitch: this.view.parameters().pitch,
      fov: this.view.parameters().fov || Math.PI / 3,
      roll: this.view.parameters().roll || 0
    };

    return viewParams;
  }

  /**
   * Get view dimensions (width and height).
   *
   * @returns View dimensions or null if not initialized.
   */
  public getViewDimensions(): { width: number; height: number } | null {
    if (!this.view) {
      return null;
    }

    return {
      width: this.view.width(),
      height: this.view.height()
    };
  }

  /**
   * Check if the view manager is properly initialized.
   *
   * @returns True if both scene and view are available.
   */
  public isInitialized(): boolean {
    return !!(this.scene && this.view);
  }

  /**
   *
   */
  public disableControls(): void {
    if (this.scene && this.scene.viewer()) {
      this.scene.viewer()?.controls()?.disable();
    }
  }

  /**
   *
   */
  public enableControls(): void {
    if (this.scene && this.scene.viewer()) {
      this.scene.viewer()?.controls()?.enable();
    }
  }

  /**
   * Dispose of the view manager and clean up references.
   */
  public dispose(): void {
    this.scene = null;
    this.view = null;
    this.logger.debug('MarzipanoView disposed');
  }
}
